/**
 * Source Management data hooks (frontend.md §5 step 11).
 *
 * Like Health, Source Management polls — `useJoplinStatusQuery` takes its interval from
 * the capability tier and pauses when the route is hidden. The action mutations
 * (`poll_now`, `markdown/import`) invalidate the status query on success.
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import { fetchJoplinStatus, triggerJoplinPoll, triggerMarkdownImport } from '@/api';
import type { JoplinSourceStatus, MarkdownImportRequest, SourceActionResult } from '@/types';
import { queryKeys } from '@/providers';
import { RENDER_DEFAULTS } from '@/config/renderDefaults';
import { useErrorHandler } from './useErrorHandler';

export function useJoplinStatusQuery(opts: {
  enabled: boolean;
}): UseQueryResult<JoplinSourceStatus, Error> {
  const { pollIntervalMs } = RENDER_DEFAULTS;
  const { handleError } = useErrorHandler();
  return useQuery({
    queryKey: queryKeys.joplinStatus(),
    enabled: opts.enabled,
    refetchInterval: opts.enabled ? pollIntervalMs : false,
    queryFn: async () => {
      try {
        return await fetchJoplinStatus();
      } catch (err) {
        handleError(err);
        throw err;
      }
    },
  });
}

export function useJoplinPollMutation(): UseMutationResult<SourceActionResult, Error, void> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: () => triggerJoplinPoll(),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.joplinStatus() });
    },
    onError: (err) => handleError(err),
  });
}

export function useMarkdownImportMutation(): UseMutationResult<
  SourceActionResult,
  Error,
  MarkdownImportRequest
> {
  const qc = useQueryClient();
  const { handleError } = useErrorHandler();
  return useMutation({
    mutationFn: (req: MarkdownImportRequest) => triggerMarkdownImport(req),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['sources'] });
    },
    onError: (err) => handleError(err),
  });
}
